import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Alert as MuiAlert,
  Button,
  DialogActions,
  Box,
  CircularProgress,
  Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import * as Yup from "yup";
import { spacing } from "@mui/system";
import styled from "@emotion/styled";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { useDialog } from "../../../../contexts/DialogContext";
import usePlan from "../../../../hooks/usePlan";
import SearchableAutoComplete from "../../../../components/SearchableAutoComplete";
import usePlanGroup from "../../../../hooks/usePlanGroup";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../../redux/slices/snackbar";
import CustomTextField from "../../../../components/CustomTextField";
import useOttPlan from "../../../../hooks/useOttPlan";
import { useTranslation } from "react-i18next";

const Alert = styled(MuiAlert)(spacing);

const MapPlanModal = ({
  planGroupId,
  isSharingAllowed,
  planGroupOttPlanId,
  isEditable = false,
  actionName = "Add",
  multiple,
  sharingType,
  ottMsoSharing,
  ottDistSharing,
  ottLcoSharing,
  serviceType,
  setPlanGroupPlanValues,
  planGroupOttPlans,
}) => {
  const initialValues = {
    planName: multiple ? [] : null,
    ottMsoSharing: ottMsoSharing,
    ottDistSharing: ottDistSharing,
    ottLcoSharing: ottLcoSharing,
    isSharingAllowed: true,
  };

  const validationSchema = Yup.object().shape({
    ottPlanName: multiple
      ? Yup.array()
          .of(
            Yup.object().shape({
              label: Yup.string().required(),
              value: Yup.number().required(),
            }),
          )
          .min(1, "You need at least one plan")
      : Yup.object()
          .shape({
            label: Yup.string().required(),
            value: Yup.number().required(),
          })
          .required("Please select the plan"),

    ottMsoSharing: Yup.number().when(["isSharingAllowed", "serviceType"], {
      is: (isSharingAllowed, serviceType) => isSharingAllowed && serviceType === "INTERNET_OTT",
      then: () =>
        Yup.number()
          .typeError("MSO sharing must be a number")
          .min(0, "Minimum value should be 0")
          .max(100, "Maximum value should be 100")
          .required("OTT MSO Sharing is required"),
    }),
    ottDistSharing: Yup.number().when(["isSharingAllowed", "serviceType"], {
      is: (isSharingAllowed, serviceType) => isSharingAllowed && serviceType === "INTERNET_OTT",
      then: () =>
        Yup.number()
          .typeError("Dist sharing must be a number")
          .min(0, "Minimum value should be 0")
          .max(100, "Maximum value should be 100")
          .required("OTT Distributor Sharing is required"),
    }),
    ottLcoSharing: Yup.number().when(["isSharingAllowed", "serviceType"], {
      is: (isSharingAllowed, serviceType) => isSharingAllowed && serviceType === "INTERNET_OTT",
      then: () =>
        Yup.number()
          .typeError("LCO sharing must be a number")
          .min(0, "Minimum value should be 0")
          .max(100, "Maximum value should be 100")
          .required("OTT LCO Sharing is required"),
    }),
  });
  const { unSetDialog } = useDialog();

  const dispatch = useDispatch();

  const { t } = useTranslation();

  const methods = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const { autoSearchLoading, ottPlanAutoSearchResults, autoSearchOttPlans, getOttPlan } =
    useOttPlan();
  const { isSubmitting, getplanGroupOttPlans, getPlanGroupPlan, planGroupPlan } = usePlanGroup();
  const currentPayload = {
    filters: [],
    pagination: {
      pageNumber: 1,
      pageSize: 100,
    },
    sortFields: [],
  };

  const [payload, setPayload] = useState(currentPayload);

  const [planGroupPlanPayload, setPlanGroupPlanPayload] = useState({
    filters: [
      {
        fieldName: "planGroupId",
        fieldValue: planGroupId,
        op: "eq",
        isJoin: true,
        dataType: "long",
      },
    ],
    pagination: {
      pageNumber: 1,
      pageSize: 100,
    },
    sortFields: [],
  });

  const [ottPlanOptions, setOttPlanOptions] = useState([]);

  const fetchData = async () => {
    await autoSearchOttPlans(payload);
  };

  const isFieldDisabled = () => {
    return sharingType === "FIXED";
  };

  const fetchPlanGroupPlan = async () => {
    await getPlanGroupPlan(planGroupOttPlanId);
  };

  useEffect(() => {
    if (ottPlanAutoSearchResults.data.length !== 0) {
      const results = [...ottPlanOptions, ...ottPlanAutoSearchResults.data];
      const filteredResults = results.filter(
        (value, index, self) => index === self.findIndex((t) => t.value === value.value),
      );
      filteredResults.sort((a, b) => a.label.localeCompare(b.label));
      setOttPlanOptions(filteredResults);
    }
  }, [ottPlanAutoSearchResults]);

  useEffect(() => {
    fetchData();
  }, [payload]);

  useEffect(() => {
    if (planGroupOttPlanId != undefined) {
      fetchPlanGroupPlan();
    }
  }, []);

  useEffect(() => {
    if (planGroupPlan != undefined && Object.keys(planGroupPlan).length != 0 && isEditable) {
      initialValues["ottPlanName"] = [
        { label: planGroupPlan.plan?.planName, value: planGroupPlan.plan?.planId },
      ];
      initialValues["planGroupOttPlanId"] = planGroupPlan?.planGroupOttPlanId;
      initialValues["msoSharing"] = planGroupPlan.msoSharing;
      initialValues["distSharing"] = planGroupPlan.distSharing;
      initialValues["lcoSharing"] = planGroupPlan.lcoSharing;
      initialValues["isSharingAllowed"] = planGroupPlan.isSharingAllowed;
      reset(initialValues);
    } else {
      reset(initialValues);
    }
  }, [planGroupPlan]);

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const selectedPlan = watch("ottPlanName");

  const fetchPlanDetails = async () => {
    await getOttPlan(selectedPlan?.value);
  };

  useEffect(() => {
    if (selectedPlan != null && !Array.isArray(selectedPlan)) {
      fetchPlanDetails();
    }
  }, [selectedPlan]);

  const onSubmit = async (data) => {
    try {
      const planGroupDetails = [];
      const totalOttSharing = data.ottMsoSharing + data.ottDistSharing + data.ottLcoSharing;

      if (totalOttSharing != 100 && isSharingAllowed && serviceType == "INTERNET_OTT") {
        dispatch(showErrorSnackbar("OTT Sharing should be equal to 100"));
        return;
      }
      const ottPlans = data.ottPlanName;

      if (Array.isArray(ottPlans)) {
        for (let i = 0; i < ottPlans.length; i++) {
          if (planExists(ottPlans[i]["value"])) {
            dispatch(showErrorSnackbar("Plan already exists"));
            return;
          }

          planGroupDetails.push({
            planGroupOttPlanId: data["planGroupOttPlanId"],
            planGroup: {
              planGroupId: planGroupId,
            },
            ottPlanId: ottPlans[i]["value"],
            ottPlanName: ottPlans[i]["label"],
            ottMsoSharing: data.ottMsoSharing,
            ottDistSharing: data.ottDistSharing,
            ottLcoSharing: data.ottLcoSharing,
            serviceType: serviceType,
            isSharingAllowed: isSharingAllowed,
            sharingType: sharingType,
          });
        }
      } else {
        if (planExists(data.ottPlanName["value"])) {
          dispatch(showErrorSnackbar("Plan already exists"));
          return;
        }
        const planDetails = await getPlan(data.planName["value"]);
        planGroupDetails.push({
          planGroupOttPlanId: data["planGroupOttPlanId"],
          planGroup: {
            planGroupId: planGroupId,
          },
          ottPlanId: data.ottPlanName["value"],
          ottPlanName: data.ottPlanName["label"],
          ottMsoSharing: data.ottMsoSharing,
          ottDistSharing: data.ottDistSharing,
          ottLcoSharing: data.ottLcoSharing,
        });
      }
      setPlanGroupPlanValues("planGroupOttPlans", [...planGroupOttPlans, ...planGroupDetails]);
      dispatch(showSuccessSnackbar("Plan mapped successfully!"));
      unSetDialog("map_ott_plan_modal");
    } catch (err) {
      console.log(err, "errerrerr");
      dispatch(showErrorSnackbar(err.message));
    }
  };
  const planExists = (planId) => {
    return planGroupOttPlans.some((p) => p.planId === planId);
  };

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ borderBottom: 1, borderColor: "divider", marginTop: 10 }}>
            <Grid container spacing={12} className="row" pt={8}>
              <Grid item xs={10} sm={12} md={4} lg={4} xl={4}>
                <SearchableAutoComplete
                  control={control}
                  data={ottPlanOptions}
                  label={t("ott_plan_name")}
                  payload={payload}
                  setPayload={setPayload}
                  loading={autoSearchLoading}
                  name="ottPlanName"
                  multiple={multiple}
                  hasNextPage={ottPlanAutoSearchResults.paging.hasNextPage}
                  disabled={isEditable}
                  fields={validationSchema.fields}
                />
              </Grid>
            </Grid>

            <Grid container spacing={12} className="row" pt={8} id="ott_sharing">
              {isSharingAllowed && (
                <Grid item md={3}>
                  <CustomTextField
                    label={t("ottMsoSharing")}
                    name="ottMsoSharing"
                    type="text"
                    my={2}
                    control={control}
                    fullWidth
                    variant="outlined"
                    errors={errors}
                    fields={validationSchema.fields}
                    disabled={isFieldDisabled()}
                  />
                </Grid>
              )}
              {isSharingAllowed && (
                <Grid item md={3}>
                  <CustomTextField
                    label={t("ottDistSharing")}
                    name="ottDistSharing"
                    type="text"
                    my={2}
                    control={control}
                    fullWidth
                    variant="outlined"
                    errors={errors}
                    fields={validationSchema.fields}
                    disabled={isFieldDisabled()}
                  />
                </Grid>
              )}
              {isSharingAllowed && (
                <Grid item md={3}>
                  <CustomTextField
                    label={t("ottLcoSharing")}
                    name="ottLcoSharing"
                    type="text"
                    my={2}
                    control={control}
                    fullWidth
                    variant="outlined"
                    errors={errors}
                    fields={validationSchema.fields}
                    disabled={isFieldDisabled()}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
          <DialogActions>
            <Button
              disabled={isSubmitting}
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              endIcon={isSubmitting ? <CircularProgress size={24} /> : null}
            >
              {actionName}
            </Button>

            <Button
              variant="text"
              onClick={() => unSetDialog("map_ott_plan_modal")}
              color="primary"
            >
              Close
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </React.Fragment>
  );
};
export default MapPlanModal;
